import React from 'react';
import { Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { RECIPIENT_CARRIER_BUCKETS } from '../../config/carriers';
import type { DashboardStats } from '../../services/dashboardService';

interface RecipientsByCarrierCardProps {
  carrierBreakdown: DashboardStats['carrierBreakdown'];
  loading: boolean;
}

export const RecipientsByCarrierCard: React.FC<RecipientsByCarrierCardProps> = ({ carrierBreakdown, loading }) => {
  // Summed from the buckets so the percentages always match the bars beside them.
  const total = RECIPIENT_CARRIER_BUCKETS.reduce((sum, c) => sum + carrierBreakdown[c.key], 0);

  return (
    <Card variant="outlined" elevation={0} sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Recipients by GSM Network
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {RECIPIENT_CARRIER_BUCKETS.map((carrier) => {
              const value = carrierBreakdown[carrier.key];
              const pct = total > 0 ? (value / total) * 100 : 0;
              return (
                <Box key={carrier.key}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: carrier.color }} />
                      <Typography variant="body2" fontWeight={500}>{carrier.label}</Typography>
                      {carrier.prefixes && (
                        <Typography variant="caption" color="text.secondary">({carrier.prefixes})</Typography>
                      )}
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      {value.toLocaleString()}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">({pct.toFixed(1)}%)</Typography>
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', bgcolor: 'action.hover', borderRadius: 1, height: 8 }}>
                    <Box sx={{ width: `${pct}%`, bgcolor: carrier.color, borderRadius: 1, height: '100%', transition: 'width 0.5s ease' }} />
                  </Box>
                </Box>
              );
            })}
            <Typography variant="caption" color="text.secondary">
              Unknown means the record holds no usable Liberian mobile number, so no SMS can reach that caregiver.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
